import type { Area } from 'react-easy-crop'

export async function createImage(url: string): Promise<HTMLImageElement> {
  const image = new Image()
  image.src = url
  await image.decode()
  return image
}

export async function getCroppedImageBlob(
  imageSrc: string,
  cropArea: Area,
  mimeType: string,
  quality = 0.95
): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  canvas.width = cropArea.width
  canvas.height = cropArea.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas context not available')
  }

  ctx.drawImage(
    image,
    cropArea.x,
    cropArea.y,
    cropArea.width,
    cropArea.height,
    0,
    0,
    cropArea.width,
    cropArea.height
  )

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to crop image'))
          return
        }
        resolve(blob)
      },
      mimeType,
      quality
    )
  })
}

export async function reduceImageFileSize(
  file: File,
  maxBytes: number,
  initialQuality = 0.92
): Promise<File> {
  const originalWithinLimit = file.size <= maxBytes

  const imageUrl = URL.createObjectURL(file)

  try {
    const image = await createImage(imageUrl)
    let scale = 1
    let quality = initialQuality
    const candidateTypes = Array.from(
      new Set([
        'image/avif',
        'image/webp',
        file.type,
        ...(file.type !== 'image/jpeg' ? ['image/jpeg'] : [])
      ])
    )

    for (let attempt = 0; attempt < 10; attempt++) {
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.floor(image.naturalWidth * scale))
      canvas.height = Math.max(1, Math.floor(image.naturalHeight * scale))

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        break
      }

      ctx.drawImage(image, 0, 0, canvas.width, canvas.height)

      const blobs = await Promise.all(
        candidateTypes.map(
          async (type) =>
            await new Promise<{ type: string; blob: Blob | null }>((resolve) => {
              canvas.toBlob((blob) => resolve({ type, blob }), type, quality)
            })
        )
      )

      const successfulBlobs = blobs.filter(
        (result): result is { type: string; blob: Blob } => result.blob !== null
      )

      if (successfulBlobs.length === 0) {
        break
      }

      const bestResult = successfulBlobs.reduce((smallest, current) =>
        current.blob.size < smallest.blob.size ? current : smallest
      )

      const type = bestResult.type
      const blob = bestResult.blob

      if (originalWithinLimit && blob.size > maxBytes && attempt === 0) {
        return file
      }

      if (blob && blob.size <= maxBytes) {
        const extension =
          type === 'image/avif'
            ? '.avif'
            : type === 'image/webp'
              ? '.webp'
              : type === 'image/jpeg'
                ? '.jpg'
                : file.name.slice(file.name.lastIndexOf('.'))
        const name =
          extension && file.name.toLowerCase().endsWith(extension.toLowerCase())
            ? file.name
            : `${file.name.replace(/\.[^.]+$/, '')}${extension}`

        return new File([blob], name, {
          type,
          lastModified: Date.now()
        })
      }

      scale *= 0.88
      quality *= 0.82
    }

    return file
  } finally {
    URL.revokeObjectURL(imageUrl)
  }
}

export async function prepareImageForCrop(
  file: File,
  maxDimension = 2200,
  quality = 0.94
): Promise<File> {
  if (!file.type.startsWith('image/')) {
    return file
  }

  const imageUrl = URL.createObjectURL(file)

  try {
    const image = await createImage(imageUrl)
    const longestSide = Math.max(image.naturalWidth, image.naturalHeight)

    if (longestSide <= maxDimension) {
      return file
    }

    const scale = maxDimension / longestSide
    const targetWidth = Math.max(1, Math.floor(image.naturalWidth * scale))
    const targetHeight = Math.max(1, Math.floor(image.naturalHeight * scale))

    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      return file
    }

    ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, file.type, quality)
    })

    if (!blob) {
      return file
    }

    return new File([blob], file.name, {
      type: blob.type || file.type,
      lastModified: Date.now()
    })
  } finally {
    URL.revokeObjectURL(imageUrl)
  }
}
